import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  LinearProgress,
  TextField,
  Divider,
  Chip,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { supabase } from '../../config/supabase'
import ServicesFormRenderer from '../../components/ServicesFormRenderer/ServicesFormRenderer'
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor'
import type { ServicesTemplateStructure } from '../../types'
import {
  validateCompanyFields,
  hasValidationErrors,
  normalizeCompanyFields,
  getPhoneError,
  getEmailError,
  getCVRError,
  getWebsiteError,
  getFacebookError,
  getInstagramError,
  getGoogleBusinessError,
  getTrustpilotError,
  type ValidationErrors,
} from '../../utils/validation'

interface TokenValidation {
  is_valid: boolean
  undertaker_id?: string
  chain_id?: string
  entity_name?: string
  template_id?: string
  already_submitted?: boolean
  expires_at?: string
}

interface ProfileData {
  phone?: string
  email?: string
  website?: string
  cvr_number?: string
  contact_person_name?: string
  contact_person_email?: string
  facebook_link?: string
  google_business_link?: string
  instagram?: string
  trustpilot?: string
  trustpilot_url?: string // chains use this field name
  usp1?: string
  usp2?: string
  usp3?: string
  description?: string
}

// Profile fields we want to collect
const PROFILE_FIELDS = [
  'phone', 'email', 'website', 'cvr_number', 'contact_person_name', 'contact_person_email',
  'facebook_link', 'google_business_link', 'instagram', 'trustpilot', 
  'usp1', 'usp2', 'usp3', 'description'
]

export default function ServicesQuestionnaire() {
  const { token } = useParams<{ token: string }>()
  
  // Step management
  const [activeStep, setActiveStep] = useState(0)
  
  // Loading states
  const [validating, setValidating] = useState(true)
  const [saving, setSaving] = useState(false)
  
  // Data states
  const [tokenData, setTokenData] = useState<TokenValidation | null>(null)
  const [template, setTemplate] = useState<ServicesTemplateStructure | null>(null)
  const [servicesData, setServicesData] = useState<Record<string, any>>({})
  const [profileData, setProfileData] = useState<ProfileData>({})
  
  // UI states
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    if (token) {
      validateToken()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const validateToken = async () => {
    try {
      setValidating(true)
      
      // Validate token
      const { data: tokenResult, error: tokenError } = await supabase
        .rpc('validate_submission_token', { p_token: token })

      if (tokenError) throw tokenError

      if (tokenResult && tokenResult.length > 0) {
        const validation = tokenResult[0] as TokenValidation
        setTokenData(validation)

        if (!validation.is_valid) {
          if (validation.expires_at && new Date(validation.expires_at) < new Date()) {
            setError('Dette link er udløbet. Kontakt Find-Bedemand.dk for at få tilsendt et nyt link.')
          } else {
            setError('Dette link er ugyldigt. Kontakt Find-Bedemand.dk for hjælp.')
          }
          return
        }
        
        if (validation.already_submitted) {
          setCompleted(true)
          return
        }

        // Fetch template
        if (validation.template_id) {
          const { data: templateData, error: templateError } = await supabase
            .from('services_templates')
            .select('template')
            .eq('id', validation.template_id)
            .single()

          if (templateError) throw templateError
          if (templateData) {
            setTemplate(templateData.template as ServicesTemplateStructure)
          }
        }

        // Contact persons are not public; the token proves who may see them
        const { data: cp } = await supabase.rpc('submission_contact_person', { p_token: token })
        const contact = (cp?.[0] ?? {}) as { contact_person_name?: string; contact_person_email?: string }

        // Fetch existing data (both services and profile)
        if (validation.chain_id) {
          const { data: chainData } = await supabase
            .from('chains')
            .select('services_data, phone, email, website, cvr_number, facebook_link, google_business_link, instagram, trustpilot_url, usp1, usp2, usp3, description')
            .eq('id', validation.chain_id)
            .single()
          
          if (chainData) {
            if (chainData.services_data?.data) {
              setServicesData(chainData.services_data.data)
            }
            // Load profile data (map trustpilot_url to trustpilot for consistency)
            setProfileData({
              phone: chainData.phone || '',
              email: chainData.email || '',
              website: chainData.website || '',
              cvr_number: chainData.cvr_number || '',
              contact_person_name: contact.contact_person_name || '',
              contact_person_email: contact.contact_person_email || '',
              facebook_link: chainData.facebook_link || '',
              google_business_link: chainData.google_business_link || '',
              instagram: chainData.instagram || '',
              trustpilot: chainData.trustpilot_url || '',
              usp1: chainData.usp1 || '',
              usp2: chainData.usp2 || '',
              usp3: chainData.usp3 || '',
              description: chainData.description || '',
            })
          }
        } else if (validation.undertaker_id) {
          const { data: undertakerData } = await supabase
            .from('undertakers')
            .select('services_data, phone, email, website, cvr_number, facebook_link, google_business_link, instagram, trustpilot, usp1, usp2, usp3, description')
            .eq('id', validation.undertaker_id)
            .single()
          
          if (undertakerData) {
            if (undertakerData.services_data?.data) {
              setServicesData(undertakerData.services_data.data)
            }
            // Load profile data
            setProfileData({
              phone: undertakerData.phone || '',
              email: undertakerData.email || '',
              website: undertakerData.website || '',
              cvr_number: undertakerData.cvr_number || '',
              contact_person_name: contact.contact_person_name || '',
              contact_person_email: contact.contact_person_email || '',
              facebook_link: undertakerData.facebook_link || '',
              google_business_link: undertakerData.google_business_link || '',
              instagram: undertakerData.instagram || '',
              trustpilot: undertakerData.trustpilot || '',
              usp1: undertakerData.usp1 || '',
              usp2: undertakerData.usp2 || '',
              usp3: undertakerData.usp3 || '',
              description: undertakerData.description || '',
            })
          }
        }

        // Mark as started
        await supabase.rpc('update_token_status', {
          p_token: token,
          p_status: 'started'
        })
      } else {
        setError('Link ikke fundet')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setValidating(false)
    }
  }

  const handleServicesChange = (fieldId: string, value: any) => {
    setServicesData(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  const handleProfileChange = (field: keyof ProfileData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Real-time validation for specific fields
    const validationFn: Record<string, (v: string) => string | null> = {
      phone: getPhoneError,
      email: getEmailError,
      contact_person_email: getEmailError,
      cvr_number: getCVRError,
      website: getWebsiteError,
      facebook_link: getFacebookError,
      instagram: getInstagramError,
      google_business_link: getGoogleBusinessError,
      trustpilot: getTrustpilotError,
    }
    
    if (validationFn[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: validationFn[field](value)
      }))
    }
  }

  // Save current data to staging (services and/or profile)
  // Data is NOT applied to the entity until admin approves it
  const saveData = async (finalize: boolean = false) => {
    if (!tokenData) return false

    // Validate profile fields before final submit
    if (finalize) {
      const errors = validateCompanyFields(profileData)
      setValidationErrors(errors)
      
      if (hasValidationErrors(errors)) {
        setError('Ret venligst fejlene i formularen før du indsender.')
        // Switch to profile tab if there are errors
        setActiveStep(1)
        return false
      }
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Prepare services data payload
      const servicesDataPayload = {
        template_id: tokenData.template_id,
        last_updated: new Date().toISOString(),
        data: servicesData
      }

      // Normalize profile data before saving
      const normalizedProfile = normalizeCompanyFields(profileData)
      
      // Prepare profile data (using consistent field names)
      const profileDataPayload = {
        phone: normalizedProfile.phone || profileData.phone || null,
        email: profileData.email || null,
        website: profileData.website || null,
        cvr_number: normalizedProfile.cvr_number || profileData.cvr_number || null,
        contact_person_name: profileData.contact_person_name || null,
        contact_person_email: profileData.contact_person_email || null,
        facebook_link: profileData.facebook_link || null,
        google_business_link: profileData.google_business_link || null,
        instagram: profileData.instagram || null,
        trustpilot: profileData.trustpilot || null, // Using consistent name, SQL will handle mapping
        usp1: profileData.usp1 || null,
        usp2: profileData.usp2 || null,
        usp3: profileData.usp3 || null,
        description: profileData.description || null,
      }

      // Save to staging (not directly to entity)
      const { data: result, error } = await supabase.rpc('save_submission_to_staging', {
        p_token: token,
        p_services_data: servicesDataPayload,
        p_profile_data: profileDataPayload,
        p_finalize: finalize
      })

      if (error) {
        console.error('RPC Error:', error)
        throw error
      }
      
      // Handle both object and array response (Supabase RPC can return both depending on definition)
      const resultObj = Array.isArray(result) ? result[0] : result
      
      if (resultObj && resultObj.success === false) {
        throw new Error(resultObj.error || 'Failed to save')
      }

      if (finalize) {
        setCompleted(true)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        setSuccess('✅ Gemt!')
        setTimeout(() => setSuccess(null), 2000)
      }
      
      return true
    } catch (err: any) {
      console.error('Save error:', err)
      setError(err.message)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return false
    } finally {
      setSaving(false)
    }
  }

  // Navigate to next step (with auto-save)
  const handleNext = async () => {
    const saved = await saveData(false)
    if (saved) {
      setActiveStep(1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Navigate to previous step (with auto-save)
  const handleBack = async () => {
    await saveData(false)
    setActiveStep(0)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Final submit
  const handleSubmit = async () => {
    await saveData(true)
  }

  // Calculate progress across both steps
  const calculateProgress = () => {
    let totalFields = 0
    let filledFields = 0

    // Count services fields
    if (template) {
      totalFields += template.sections.reduce((acc, s) => acc + s.fields.length, 0)
      
      // Count filled services fields
      const allFieldIds = template.sections.flatMap(s => s.fields.map(f => f.id))
      filledFields += allFieldIds.filter(fieldId => {
        const val = servicesData[fieldId]
        if (val === null || val === undefined || val === '') return false
        if (typeof val === 'object' && Object.keys(val).length === 0) return false
        return true
      }).length
    }

    // Count profile fields (11 fields total)
    totalFields += PROFILE_FIELDS.length
    filledFields += PROFILE_FIELDS.filter(field => {
      const val = profileData[field as keyof ProfileData]
      return val && val.toString().trim() !== ''
    }).length

    if (totalFields === 0) return 0
    return Math.round((filledFields / totalFields) * 100)
  }

  // Loading state
  if (validating) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        gap: 2
      }}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">Validerer link...</Typography>
      </Box>
    )
  }

  // Error state
  if (error && !tokenData?.is_valid) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Card sx={{ maxWidth: 500 }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h5" gutterBottom color="error">
              Link ugyldigt
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {error || 'Dette link er udløbet eller ugyldigt. Kontakt Find-Bedemand.dk for et nyt link.'}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    )
  }

  // Completed state
  if (completed) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Card sx={{ maxWidth: 600 }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Tak for din indsendelse!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Dine oplysninger er modtaget og afventer gennemgang. Vi opdaterer din profil hurtigst muligt.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Du kan nu lukke denne side.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    )
  }

  // No template state
  if (!template) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Alert severity="error">Kunne ikke indlæse skabelon</Alert>
      </Box>
    )
  }

  const progress = calculateProgress()

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto', px: 3 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                {activeStep === 0 ? 'Services & Priser' : 'Profil & Kontakt'}
              </Typography>
              <Typography variant="body1" color="primary" sx={{ fontWeight: 500 }}>
                {tokenData?.entity_name}
              </Typography>
            </Box>
            
            {/* Step indicator */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label="1. Services" 
                color={activeStep === 0 ? 'primary' : 'default'}
                variant={activeStep === 0 ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip 
                label="2. Profil" 
                color={activeStep === 1 ? 'primary' : 'default'}
                variant={activeStep === 1 ? 'filled' : 'outlined'}
                size="small"
              />
            </Box>
          </Box>
          
          {/* Progress bar */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Samlet fremskridt
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Step 1: Services */}
        {activeStep === 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Udfyld jeres services, pakker og priser. Du kan gemme undervejs og vende tilbage senere.
            </Typography>
            
            <ServicesFormRenderer
              template={template}
              data={servicesData}
              onChange={handleServicesChange}
              readOnly={false}
            />

            {/* Step 1 Navigation */}
            <Paper sx={{ p: 3, mt: 3, position: 'sticky', bottom: 16 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Trin 1 af 2
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={saving}
                  endIcon={saving ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                >
                  {saving ? 'Gemmer...' : 'Gem og næste side'}
                </Button>
              </Box>
            </Paper>
          </>
        )}

        {/* Step 2: Profile */}
        {activeStep === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Opdater jeres kontaktoplysninger og profiltekster.
            </Typography>

            {/* Contact Information */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Kontaktoplysninger
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={profileData.phone || ''}
                    onChange={handleProfileChange('phone')}
                    placeholder="12 34 56 78"
                    error={!!validationErrors.phone}
                    helperText={validationErrors.phone || 'F.eks. 12 34 56 78'}
                  />
                  <TextField
                    fullWidth
                    label="E-mail (offentlig)"
                    type="email"
                    value={profileData.email || ''}
                    onChange={handleProfileChange('email')}
                    placeholder="kontakt@eksempel.dk"
                    error={!!validationErrors.email}
                    helperText={validationErrors.email || 'Vises på jeres profil'}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={profileData.website || ''}
                    onChange={handleProfileChange('website')}
                    placeholder="https://www.eksempel.dk"
                    error={!!validationErrors.website}
                    helperText={validationErrors.website}
                  />
                  <TextField
                    fullWidth
                    label="CVR-nummer"
                    value={profileData.cvr_number || ''}
                    onChange={handleProfileChange('cvr_number')}
                    placeholder="12345678"
                    error={!!validationErrors.cvr_number}
                    helperText={validationErrors.cvr_number || '8 cifre'}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Internal Contact Person */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Kontaktperson (intern)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Disse oplysninger er kun til intern brug og vises ikke offentligt.
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Kontaktpersonens navn"
                  value={profileData.contact_person_name || ''}
                  onChange={handleProfileChange('contact_person_name')}
                  placeholder="F.eks. Anders Andersen"
                />
                <TextField
                  fullWidth
                  label="Kontaktpersonens e-mail"
                  type="email"
                  value={profileData.contact_person_email || ''}
                  onChange={handleProfileChange('contact_person_email')}
                  placeholder="anders@eksempel.dk"
                  error={!!validationErrors.contact_person_email}
                  helperText={validationErrors.contact_person_email}
                />
              </Box>
            </Paper>

            {/* Social Links */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Sociale medier & anmeldelser
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    value={profileData.facebook_link || ''}
                    onChange={handleProfileChange('facebook_link')}
                    placeholder="https://facebook.com/jeresfirma"
                    error={!!validationErrors.facebook_link}
                    helperText={validationErrors.facebook_link || 'Link til jeres Facebook side'}
                  />
                  <TextField
                    fullWidth
                    label="Instagram"
                    value={profileData.instagram || ''}
                    onChange={handleProfileChange('instagram')}
                    placeholder="@brugernavn"
                    error={!!validationErrors.instagram}
                    helperText={validationErrors.instagram || '@brugernavn eller https://instagram.com/brugernavn'}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Google Business"
                    value={profileData.google_business_link || ''}
                    onChange={handleProfileChange('google_business_link')}
                    placeholder="https://g.page/jeresfirma"
                    error={!!validationErrors.google_business_link}
                    helperText={validationErrors.google_business_link || 'Link fra Google Maps'}
                  />
                  <TextField
                    fullWidth
                    label="Trustpilot"
                    value={profileData.trustpilot || ''}
                    onChange={handleProfileChange('trustpilot')}
                    placeholder="https://dk.trustpilot.com/review/jeresfirma"
                    error={!!validationErrors.trustpilot}
                    helperText={validationErrors.trustpilot || 'Link til jeres Trustpilot profil'}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Marketing */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Hvad adskiller jer fra andre bedemænd?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Skriv 1-3 korte kendetegn der gør jer unikke. De vises på jeres profil så pårørende hurtigt kan se hvad I står for.
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="USP 1"
                  value={profileData.usp1 || ''}
                  onChange={handleProfileChange('usp1')}
                  placeholder="F.eks. 'Lokal og personlig'"
                  inputProps={{ maxLength: 30 }}
                  helperText={`${(profileData.usp1 || '').length}/30 tegn`}
                />
                <TextField
                  fullWidth
                  label="USP 2"
                  value={profileData.usp2 || ''}
                  onChange={handleProfileChange('usp2')}
                  placeholder="F.eks. 'Døgnservice'"
                  inputProps={{ maxLength: 30 }}
                  helperText={`${(profileData.usp2 || '').length}/30 tegn`}
                />
                <TextField
                  fullWidth
                  label="USP 3"
                  value={profileData.usp3 || ''}
                  onChange={handleProfileChange('usp3')}
                  placeholder="F.eks. 'Erfaren og omsorgsfuld'"
                  inputProps={{ maxLength: 30 }}
                  helperText={`${(profileData.usp3 || '').length}/30 tegn`}
                />
              </Box>
            </Paper>

            {/* Description */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Beskrivelse
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Skriv en beskrivelse af jeres virksomhed. Denne tekst vises på jeres profil.
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <RichTextEditor
                value={profileData.description || ''}
                onChange={(html) => setProfileData(prev => ({ ...prev, description: html }))}
                placeholder="Fortæl potentielle kunder om jeres virksomhed, jeres værdier, erfaring og hvad der gør jer unikke..."
                minHeight={200}
              />
              
              {/* Word counter */}
              {(() => {
                const text = (profileData.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
                const wordCount = text ? text.split(' ').filter(w => w.length > 0).length : 0
                const isGood = wordCount >= 150
                return (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isGood ? 'success.main' : 'text.secondary',
                        fontWeight: isGood ? 600 : 400
                      }}
                    >
                      {wordCount} ord
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isGood ? '✓ God længde!' : '(150+ ord anbefales)'}
                    </Typography>
                  </Box>
                )
              })()}
            </Paper>

            {/* Step 2 Navigation */}
            <Paper sx={{ p: 3, mt: 3, position: 'sticky', bottom: 16 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={saving}
                  startIcon={<ArrowBackIcon />}
                >
                  Tilbage
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Trin 2 af 2
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {saving ? 'Gemmer...' : 'Indsend'}
                </Button>
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  )
}
